// GENERATED from messaging-platform/packages/chat-core/src/avatar.ts. Do not edit here: edit there, then run scripts/sync-chat-core.cjs.
import type { ConversationSummary } from './hub-types';

/**
 * Avatars in the conversation list (2026-09-20). No pictures anywhere in the ecosystem yet, so a person is their
 * initials on a colour derived from their id: the same person is the same colour in every app, and two people with the
 * same initials rarely collide. Channels, patient chats, referral chats and support keep an icon instead, since "SC"
 * for a channel called "Scheduling" reads as a person.
 */

/** "Dr Jane Mary Doe" -> "JD"; a single name -> its first two letters; nothing usable -> "?". */
export function initialsOf(name: string | null | undefined): string {
  const words = String(name ?? '')
    .replace(/\(.*?\)/g, ' ')
    .split(/[\s,]+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter((w) => w.length > 0 && !TITLES.has(w.toLowerCase()));
  if (!words.length) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}

const TITLES = new Set(['dr', 'dr.', 'mr', 'mr.', 'mrs', 'mrs.', 'ms', 'ms.', 'miss', 'prof', 'prof.', 'sr', 'sr.', 'mx']);

/**
 * The hues people are drawn in: ten spread round the wheel so that neighbours read as different colours (red, orange,
 * amber, green, teal, sky, blue, violet, purple, pink). Greens and blues get fewer slots than their share of the wheel,
 * since eyes tell those apart least; any hue at all made two greens or two blues a few degrees apart look the same.
 */
export const AVATAR_HUES: readonly number[] = [0, 28, 48, 130, 175, 202, 228, 258, 288, 328];

/** A hue for an id or name, from AVATAR_HUES: the same person is the same colour in every app. */
export function avatarHue(seed: string | null | undefined): number {
  return AVATAR_HUES[hashOf(String(seed ?? '')) % AVATAR_HUES.length]!;
}

/**
 * Colours for the people in one conversation, in the order they joined. Each keeps their usual colour unless someone
 * who joined earlier already has it; then they take the next free one, so no two people in a group share a colour
 * (until there are more people than colours) and nobody's colour changes when someone joins after them.
 */
export function memberHues(principalIdsInJoinOrder: readonly string[]): Map<string, number> {
  const hues = new Map<string, number>();
  const taken = new Set<number>();
  for (const id of principalIdsInJoinOrder) {
    if (hues.has(id)) continue;
    const own = AVATAR_HUES.indexOf(avatarHue(id));
    let hue = AVATAR_HUES[own]!;
    for (let step = 0; step < AVATAR_HUES.length && taken.has(hue); step++) hue = AVATAR_HUES[(own + step + 1) % AVATAR_HUES.length]!;
    if (taken.has(hue)) hue = AVATAR_HUES[own]!;
    taken.add(hue);
    hues.set(id, hue);
  }
  return hues;
}

/** FNV-1a with a final mix, so ids that differ in a character or two still land on different colours. */
function hashOf(s: string): number {
  let x = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    x ^= s.charCodeAt(i);
    x = Math.imul(x, 0x01000193) >>> 0;
  }
  x ^= x >>> 16;
  x = Math.imul(x, 0x85ebca6b) >>> 0;
  x ^= x >>> 13;
  x = Math.imul(x, 0xc2b2ae35) >>> 0;
  x ^= x >>> 16;
  return x >>> 0;
}

export interface AvatarView {
  /** initials to show, or null when an icon says it better */
  initials: string | null;
  /** a Material icon name when there are no initials */
  icon: string | null;
  hue: number;
}

/**
 * The profile photo to show for a conversation: the other person's in a direct chat, nobody's in a room (a room shows
 * its icon, and each message shows its own sender's photo). Pair it with `ChatStore.avatarUrl`.
 */
export function photoOf(c: ConversationSummary, myPrincipalId: string | null | undefined): { principalId: string; avatar: string } | null {
  if (c.type !== 'direct') return null;
  const other = (c.members ?? []).find((m) => m.principalId !== myPrincipalId && m.avatar);
  return other?.avatar ? { principalId: other.principalId, avatar: other.avatar } : null;
}

/** A group's own photo, or null: only a group (channel) has one. Pair it with `ChatStore.conversationAvatarUrl`. */
export function conversationPhotoRef(c: ConversationSummary): string | null {
  return c.type === 'channel' && c.avatar ? c.avatar : null;
}

/**
 * What to draw beside a conversation when there is no photo: a person's initials for a direct message, an icon for a
 * channel, a group, a patient chat, a referral chat or support. `myPrincipalId` keeps you out of your own DM's initials.
 */
export function avatarOf(c: ConversationSummary, myPrincipalId: string | null | undefined): AvatarView {
  const others = (c.members ?? []).filter((m) => m.principalId !== myPrincipalId);
  switch (c.type) {
    case 'channel':
      return { initials: null, icon: c.visibility === 'private' ? 'lock' : 'groups', hue: avatarHue(c.id) };
    case 'patient':
      return { initials: null, icon: 'personal_injury', hue: avatarHue(c.id) };
    case 'referral':
      return { initials: null, icon: 'swap_horiz', hue: avatarHue(c.id) };
    case 'support':
      return { initials: null, icon: 'support_agent', hue: avatarHue(c.id) };
    case 'group_dm':
      return { initials: null, icon: 'group', hue: avatarHue(c.id) };
    default: {
      const person = others[0];
      return { initials: initialsOf(person?.name ?? c.name), hue: avatarHue(person?.principalId ?? c.id), icon: null };
    }
  }
}
